import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users, FileText, Calendar, Activity, TrendingUp, Heart, ShoppingCart, PawPrint } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const OverviewTab = ({ filters, onViewDetailed }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadOverviewStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getOverviewStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load overview stats:', err);
            setError(err.message || t('systemDashboard.overview.loadError'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        loadOverviewStats();
    }, [loadOverviewStats]);

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadOverviewStats} className="retry-button">{t('systemDashboard.overview.retry')}</button>
            </div>
        );
    }

    const growthData = stats?.graph_data?.visitor_growth || [];
    const deviceData = stats?.graph_data?.device_stats || [];

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    {t('systemDashboard.overview.detailed')} <ArrowRight size={14} />
                </button>
            </div>

            {/* Primary KPI Cards */}
            <div className="sd-grid sd-grid--kpi-primary">
                <StatCard
                    icon={Users}
                    label={t('systemDashboard.overview.totalUsers')}
                    value={stats?.totalUsers || 0}
                    trend={stats?.trends?.userGrowth}
                    trendLabel={t('systemDashboard.overview.vsLastPeriod')}
                    color="bg-blue-500"
                    loading={loading}
                />
                <StatCard
                    icon={FileText}
                    label={t('systemDashboard.overview.totalPosts')}
                    value={stats?.totalPosts || 0}
                    trend={stats?.trends?.postEngagement}
                    trendLabel={t('systemDashboard.overview.engagement')}
                    color="bg-violet-500"
                    loading={loading}
                />
                <StatCard
                    icon={Calendar}
                    label={t('systemDashboard.overview.appointments')}
                    value={stats?.totalAppointments || 0}
                    trend={stats?.trends?.appointmentRate}
                    trendLabel={t('systemDashboard.overview.completion')}
                    color="bg-rose-500"
                    loading={loading}
                />
                <StatCard
                    icon={Activity}
                    label={t('systemDashboard.overview.activeClinics')}
                    value={stats?.activeClinics || 0}
                    color="bg-teal-500"
                    loading={loading}
                />
            </div>

            {/* Secondary KPI Cards */}
            <div className="sd-grid sd-grid--kpi-secondary">
                <StatCard
                    icon={PawPrint}
                    label={t('systemDashboard.overview.totalPets')}
                    value={stats?.totalPets || 0}
                    color="bg-orange-500"
                    loading={loading}
                />
                <StatCard
                    icon={Heart}
                    label={t('systemDashboard.overview.adoptionPosts')}
                    value={stats?.adoptionPosts || 0}
                    color="bg-pink-500"
                    loading={loading}
                />
                <StatCard
                    icon={ShoppingCart}
                    label={t('systemDashboard.overview.marketplaceOrders')}
                    value={stats?.marketplaceOrders || 0}
                    color="bg-emerald-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts sd-grid--last">
                    <SectionCard title={t('systemDashboard.overview.visitorGrowth')} icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={growthData} color="#8b5cf6" title="" />
                    </SectionCard>
                    <SectionCard title={t('systemDashboard.overview.deviceUsage')} icon={Activity}>
                        <CustomDonutChart
                            data={deviceData}
                            totalValue={deviceData.reduce((a, b) => a + b.value, 0)}
                            title=""
                        />
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
